/**
 * Provides the authenticated, runtime-validated API boundary for the backoffice.
 */
import { NotificationListQuerySchema, NotificationListResponseSchema, NotificationMarkAllReadResponseSchema, NotificationReadResponseSchema, } from "@libro-reclamos/contracts/Async";
import { CompleteUploadRequestSchema, CreateImportRequestSchema, CreateReportRequestSchema, EvidenceUploadRequestSchema, FileStatusResponseSchema, ImportPreviewResponseSchema, ImportResponseSchema, ReportResponseSchema, TemporaryDownloadSchema, UploadAuthorizationSchema, } from "@libro-reclamos/contracts/Artifacts";
import { BookNoticeSchema } from "@libro-reclamos/contracts/BookNotices";
import { BackofficeLoginRequestSchema, BackofficeSessionResponseSchema, } from "@libro-reclamos/contracts/Identity";
import { CompanyBranchSchema, CompanyDetailSchema, CompanyDomainSchema, CompanyListResponseSchema, CompanyProvisioningResultSchema, CreateCompanyBranchRequestSchema, CreateCompanyDomainRequestSchema, CreateCompanyRequestSchema, DomainVerificationResultSchema, PreviewCompanyBrandingLogoRequestSchema, PreviewCompanyBrandingLogoResultSchema, ResetCompanyAdministratorCredentialsRequestSchema, ResetCompanyAdministratorCredentialsResultSchema, UpdateCompanyBranchRequestSchema, UpdateCompanyBrandingRequestSchema, UpdateCompanyRequestSchema, CompanyProfileSchema, UpdateCompanyProfileRequestSchema, } from "@libro-reclamos/contracts/Platform";
import { AssignCompanyPlanRequestSchema, CompanyEntitlementsSchema, PlanListResponseSchema, PlanSchema, UpdatePlanRequestSchema, } from "@libro-reclamos/contracts/Plans";
import { AddInternalCaseNoteRequestSchema, AddInternalCaseTagRequestSchema, AssignInternalCaseRequestSchema, ChangeInternalCaseBranchRequestSchema, ChangeInternalCaseNoteColorRequestSchema, ChangeInternalCasePriorityRequestSchema, CorrectInternalCaseNoteRequestSchema, InternalCaseDetailResponseSchema, InternalCaseListQuerySchema, InternalCaseListResponseSchema, InternalCaseMutationResponseSchema, ReclassifyInternalCaseRequestSchema, SetInternalCaseReasonRequestSchema, RemoveInternalCaseTagRequestSchema, ReopenInternalCaseRequestSchema, RevealCaseAccessTokenResponseSchema, TransitionInternalCaseRequestSchema, } from "@libro-reclamos/contracts/InternalCases";
import { CaseResponseWorkflowSchema, ConfirmEmailResponseRequestSchema, DecideResponseDraftRequestSchema, DispatchLetterResponseRequestSchema, ResponseMutationResultSchema, SaveResponseDraftRequestSchema, SubmitResponseDraftRequestSchema, WithdrawResponseDraftRequestSchema, } from "@libro-reclamos/contracts/Responses";
import { RealtimeNotificationEventName, RealtimeNotificationEventSchema, } from "@libro-reclamos/contracts/Realtime";
import { CreateEscalationLevelRequestSchema, EscalationLevelItemSchema, EscalationLevelListResponseSchema, UpdateEscalationLevelRequestSchema, } from "@libro-reclamos/contracts/Escalations";
import { CompanyTagListResponseSchema, CompanyTagSchema, CreateCompanyTagRequestSchema, UpdateCompanyTagRequestSchema, } from "@libro-reclamos/contracts/Tags";
import { CaseReasonAnalyticsResponseSchema, CaseReasonCatalogResponseSchema, CreateCaseReasonCategoryRequestSchema, CreateCaseReasonRequestSchema, CreateCaseSubReasonRequestSchema, UpdateCaseReasonNodeRequestSchema, UpdateCompanyReasonPolicyRequestSchema, } from "@libro-reclamos/contracts/CaseReasons";
import { CancelCaseFollowUpRequestSchema, CaseFollowUpItemSchema, CaseFollowUpListQuerySchema, CaseFollowUpListResponseSchema, CreateCaseFollowUpRequestSchema, RescheduleCaseFollowUpRequestSchema, WorkspaceFollowUpListQuerySchema, WorkspaceFollowUpListResponseSchema, } from "@libro-reclamos/contracts/FollowUps";
import { CompanyBranchListResponseSchema, CompanyUserListResponseSchema, CompanyUserSchema, CreateCompanyUserRequestSchema, MembershipMutationReasonSchema, UpdateCompanyUserRequestSchema, } from "@libro-reclamos/contracts/Memberships";
import axios, {} from "axios";
import { io } from "socket.io-client";
import { z } from "zod";
const problemSchema = z
    .object({
    code: z.string().optional(),
    detail: z.string().optional(),
})
    .loose();
export class InternalApiError extends Error {
    code;
    status;
    constructor(message, status, code, options) {
        super(message, options);
        this.name = "InternalApiError";
        this.code = code;
        this.status = status;
    }
}
function PublicIdPath(publicId) {
    if (!/^\d+$/.test(publicId)) {
        throw new Error("The internal case public identifier is invalid.");
    }
    return `/cases/${encodeURIComponent(publicId)}`;
}
function UuidPath(value, label) {
    if (!z.uuid().safeParse(value).success) {
        throw new Error(`${label} identifier is invalid.`);
    }
    return encodeURIComponent(value);
}
function ToInternalError(error) {
    if (!axios.isAxiosError(error)) {
        return new InternalApiError("The internal service could not complete the request.", undefined, undefined, {
            cause: error,
        });
    }
    const problem = problemSchema.safeParse(error.response?.data);
    return new InternalApiError(problem.success
        ? (problem.data.detail ?? "The internal service rejected the request.")
        : "The internal service could not complete the request.", error.response?.status, problem.success ? problem.data.code : undefined, { cause: error });
}
/**
 * Calculates the SHA-256 required by upload completion without exposing file contents to the API.
 */
export async function Sha256Blob(body) {
    const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", await body.arrayBuffer()));
    return [...digest].map((value) => value.toString(16).padStart(2, "0")).join("");
}
/**
 * Keeps the short-lived access token only in memory and rotates through the HttpOnly cookie.
 */
export function CreateInternalCaseApiClient(options) {
    if (options.timeoutMs <= 0) {
        throw new Error("The internal API timeout must be positive.");
    }
    const instance = axios.create({
        baseURL: new URL(options.baseUrl).toString().replace(/\/$/, ""),
        headers: { accept: "application/json", "content-type": "application/json" },
        timeout: options.timeoutMs,
        withCredentials: true,
    });
    const realtimeOrigin = new URL(options.baseUrl).origin;
    let accessToken = null;
    let refreshPromise = null;
    async function Refresh() {
        if (refreshPromise !== null) {
            return refreshPromise;
        }
        refreshPromise = instance
            .post("/auth/refresh")
            .then((response) => {
            const session = BackofficeSessionResponseSchema.parse(response.data);
            accessToken = session.accessToken;
        })
            .finally(() => {
            refreshPromise = null;
        });
        return refreshPromise;
    }
    async function Authenticated(operation) {
        if (accessToken === null) {
            try {
                await Refresh();
            }
            catch (error) {
                throw ToInternalError(error);
            }
        }
        try {
            return await operation();
        }
        catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                try {
                    await Refresh();
                    return await operation();
                }
                catch (retryError) {
                    throw ToInternalError(retryError);
                }
            }
            throw ToInternalError(error);
        }
    }
    function AuthorizationHeaders() {
        if (accessToken === null) {
            throw new InternalApiError("An authenticated session is required.", 401);
        }
        return { authorization: `Bearer ${accessToken}` };
    }
    async function Mutate(path, input, parseBody) {
        const body = parseBody(input.body);
        return Authenticated(async () => {
            const response = await instance.post(`${PublicIdPath(input.publicId)}${path}`, body, {
                headers: {
                    ...AuthorizationHeaders(),
                    ...(input.idempotencyKey === undefined
                        ? {}
                        : { "idempotency-key": input.idempotencyKey }),
                },
                ...(input.signal === undefined ? {} : { signal: input.signal }),
            });
            return InternalCaseMutationResponseSchema.parse(response.data);
        });
    }
    async function MutateResponse(path, input, parseBody) {
        const body = parseBody(input.body);
        return Authenticated(async () => {
            const response = await instance.post(`${PublicIdPath(input.publicId)}${path}`, body, {
                headers: {
                    ...AuthorizationHeaders(),
                    ...(input.idempotencyKey === undefined
                        ? {}
                        : { "idempotency-key": input.idempotencyKey }),
                },
                ...(input.signal === undefined ? {} : { signal: input.signal }),
            });
            return ResponseMutationResultSchema.parse(response.data);
        });
    }
    const client = {
        async createEvidenceUpload(publicId, input) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/evidences/uploads`, EvidenceUploadRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return UploadAuthorizationSchema.parse(response.data);
            });
        },
        async completeEvidenceUpload(fileId, input) {
            return Authenticated(async () => {
                const response = await instance.post(`/evidences/${UuidPath(fileId, "file")}/complete`, CompleteUploadRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return FileStatusResponseSchema.parse(response.data);
            });
        },
        async fileStatus(fileId) {
            return Authenticated(async () => {
                const response = await instance.get(`/evidences/${UuidPath(fileId, "file")}`, {
                    headers: AuthorizationHeaders(),
                });
                return FileStatusResponseSchema.parse(response.data);
            });
        },
        async downloadFile(fileId) {
            return Authenticated(async () => {
                const response = await instance.get(`/files/${UuidPath(fileId, "file")}/download`, { headers: AuthorizationHeaders() });
                return TemporaryDownloadSchema.parse(response.data);
            });
        },
        async createReport(input) {
            return Authenticated(async () => {
                const response = await instance.post("/reports", CreateReportRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return ReportResponseSchema.parse(response.data);
            });
        },
        async listTags(includeInactive = true, signal) {
            return Authenticated(async () => {
                const response = await instance.get("/tags", {
                    headers: AuthorizationHeaders(),
                    params: { includeInactive: includeInactive ? "true" : "false" },
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyTagListResponseSchema.parse(response.data);
            });
        },
        async bookNotice(branchSlug, signal) {
            return Authenticated(async () => {
                const response = await instance.get("/book-notice", {
                    headers: AuthorizationHeaders(),
                    ...(branchSlug === undefined ? {} : { params: { branchSlug } }),
                    ...(signal === undefined ? {} : { signal }),
                });
                return BookNoticeSchema.parse(response.data);
            });
        },
        async bookNoticePdf(branchSlug, signal) {
            return Authenticated(async () => {
                const response = await instance.get("/book-notice/pdf", {
                    headers: { ...AuthorizationHeaders(), accept: "application/pdf" },
                    ...(branchSlug === undefined ? {} : { params: { branchSlug } }),
                    responseType: "blob",
                    ...(signal === undefined ? {} : { signal }),
                });
                return response.data;
            });
        },
        async listCompanyBranches(signal) {
            return Authenticated(async () => {
                const response = await instance.get("/memberships/branches", {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyBranchListResponseSchema.parse(response.data);
            });
        },
        async listCompanyUsers(includeInactive = true, signal) {
            return Authenticated(async () => {
                const response = await instance.get("/memberships/users", {
                    headers: AuthorizationHeaders(),
                    params: { includeInactive: includeInactive ? "true" : "false" },
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyUserListResponseSchema.parse(response.data);
            });
        },
        async createCompanyUser(input) {
            return Authenticated(async () => {
                const response = await instance.post("/memberships/users", CreateCompanyUserRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CompanyUserSchema.parse(response.data);
            });
        },
        async updateCompanyUser(membershipId, input) {
            return Authenticated(async () => {
                const response = await instance.patch(`/memberships/users/${UuidPath(membershipId, "membership")}`, UpdateCompanyUserRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CompanyUserSchema.parse(response.data);
            });
        },
        async deactivateCompanyUser(membershipId, body = {}) {
            return Authenticated(async () => {
                const response = await instance.post(`/memberships/users/${UuidPath(membershipId, "membership")}/deactivate`, MembershipMutationReasonSchema.parse(body), { headers: AuthorizationHeaders() });
                return CompanyUserSchema.parse(response.data);
            });
        },
        async activateCompanyUser(membershipId, body = {}) {
            return Authenticated(async () => {
                const response = await instance.post(`/memberships/users/${UuidPath(membershipId, "membership")}/activate`, MembershipMutationReasonSchema.parse(body), { headers: AuthorizationHeaders() });
                return CompanyUserSchema.parse(response.data);
            });
        },
        async listEscalationLevels(signal) {
            return Authenticated(async () => {
                const response = await instance.get("/escalation-levels", {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return EscalationLevelListResponseSchema.parse(response.data);
            });
        },
        async createEscalationLevel(input) {
            return Authenticated(async () => {
                const response = await instance.post("/escalation-levels", CreateEscalationLevelRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return EscalationLevelItemSchema.parse(response.data);
            });
        },
        async updateEscalationLevel(levelId, input) {
            return Authenticated(async () => {
                const response = await instance.put(`/escalation-levels/${UuidPath(levelId, "escalation level")}`, UpdateEscalationLevelRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return EscalationLevelItemSchema.parse(response.data);
            });
        },
        async deleteEscalationLevel(levelId) {
            return Authenticated(async () => {
                await instance.delete(`/escalation-levels/${UuidPath(levelId, "escalation level")}`, { headers: AuthorizationHeaders() });
            });
        },
        async createTag(input) {
            return Authenticated(async () => {
                const response = await instance.post("/tags", CreateCompanyTagRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CompanyTagSchema.parse(response.data);
            });
        },
        async updateTag(tagId, input) {
            return Authenticated(async () => {
                const response = await instance.patch(`/tags/${UuidPath(tagId, "tag")}`, UpdateCompanyTagRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CompanyTagSchema.parse(response.data);
            });
        },
        async deactivateTag(tagId) {
            return Authenticated(async () => {
                const response = await instance.post(`/tags/${UuidPath(tagId, "tag")}/deactivate`, undefined, { headers: AuthorizationHeaders() });
                return CompanyTagSchema.parse(response.data);
            });
        },
        async activateTag(tagId) {
            return Authenticated(async () => {
                const response = await instance.post(`/tags/${UuidPath(tagId, "tag")}/activate`, undefined, { headers: AuthorizationHeaders() });
                return CompanyTagSchema.parse(response.data);
            });
        },
        async caseReasonCatalog(includeInactive = true, signal) {
            return Authenticated(async () => {
                const response = await instance.get("/case-reasons", {
                    headers: AuthorizationHeaders(),
                    params: { includeInactive: includeInactive ? "true" : "false" },
                    ...(signal === undefined ? {} : { signal }),
                });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async caseReasonAnalytics(query, signal) {
            return Authenticated(async () => {
                const response = await instance.get("/case-reasons/analytics", {
                    headers: AuthorizationHeaders(),
                    params: query ?? {},
                    ...(signal === undefined ? {} : { signal }),
                });
                return CaseReasonAnalyticsResponseSchema.parse(response.data);
            });
        },
        async createCaseReasonCategory(input) {
            return Authenticated(async () => {
                const response = await instance.post("/case-reasons/categories", CreateCaseReasonCategoryRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async createCaseReason(input) {
            return Authenticated(async () => {
                const response = await instance.post("/case-reasons/reasons", CreateCaseReasonRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async createCaseSubReason(input) {
            return Authenticated(async () => {
                const response = await instance.post("/case-reasons/sub-reasons", CreateCaseSubReasonRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async updateCaseReasonNode(level, nodeId, input) {
            return Authenticated(async () => {
                const response = await instance.patch(`/case-reasons/${level}/${UuidPath(nodeId, "case reason")}`, UpdateCaseReasonNodeRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async activateCaseReasonNode(level, nodeId) {
            return Authenticated(async () => {
                const response = await instance.post(`/case-reasons/${level}/${UuidPath(nodeId, "case reason")}/activate`, undefined, { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async deactivateCaseReasonNode(level, nodeId) {
            return Authenticated(async () => {
                const response = await instance.post(`/case-reasons/${level}/${UuidPath(nodeId, "case reason")}/deactivate`, undefined, { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async updateCaseReasonPolicy(input) {
            return Authenticated(async () => {
                const response = await instance.put("/case-reasons/policy", UpdateCompanyReasonPolicyRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return CaseReasonCatalogResponseSchema.parse(response.data);
            });
        },
        async reportStatus(reportId) {
            return Authenticated(async () => {
                const response = await instance.get(`/reports/${UuidPath(reportId, "report")}`, {
                    headers: AuthorizationHeaders(),
                });
                return ReportResponseSchema.parse(response.data);
            });
        },
        async createImport(input) {
            return Authenticated(async () => {
                const response = await instance.post("/imports", CreateImportRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return UploadAuthorizationSchema.parse(response.data);
            });
        },
        async completeImportUpload(fileId, input) {
            return Authenticated(async () => {
                const response = await instance.post(`/imports/uploads/${UuidPath(fileId, "file")}/complete`, CompleteUploadRequestSchema.parse(input), { headers: AuthorizationHeaders() });
                return ImportResponseSchema.parse(response.data);
            });
        },
        async importStatus(importId) {
            return Authenticated(async () => {
                const response = await instance.get(`/imports/${UuidPath(importId, "import")}`, {
                    headers: AuthorizationHeaders(),
                });
                return ImportResponseSchema.parse(response.data);
            });
        },
        async importPreview(importId) {
            return Authenticated(async () => {
                const response = await instance.get(`/imports/${UuidPath(importId, "import")}/preview`, { headers: AuthorizationHeaders() });
                return ImportPreviewResponseSchema.parse(response.data);
            });
        },
        async applyImport(importId) {
            return Authenticated(async () => {
                const response = await instance.post(`/imports/${UuidPath(importId, "import")}/apply`, undefined, { headers: AuthorizationHeaders() });
                return ImportResponseSchema.parse(response.data);
            });
        },
        async uploadPrivateObject(authorization, body) {
            const target = UploadAuthorizationSchema.parse(authorization);
            if (body.size <= 0) {
                throw new Error("The private upload body must not be empty.");
            }
            try {
                await axios.put(target.url, body, {
                    headers: target.headers,
                    timeout: options.timeoutMs,
                    withCredentials: false,
                });
            }
            catch (error) {
                throw ToInternalError(error);
            }
        },
        async platformAccessEnabled() {
            try {
                const response = await instance.get("/auth/platform/status");
                return response.data.enabled === true;
            }
            catch {
                return false;
            }
        },
        async login(input) {
            try {
                const response = await instance.post("/auth/login", BackofficeLoginRequestSchema.parse(input));
                const session = BackofficeSessionResponseSchema.parse(response.data);
                accessToken = session.accessToken;
                return session;
            }
            catch (error) {
                throw ToInternalError(error);
            }
        },
        async logout() {
            try {
                if (accessToken !== null) {
                    await instance.post("/auth/logout", undefined, {
                        headers: AuthorizationHeaders(),
                    });
                }
            }
            catch (error) {
                throw ToInternalError(error);
            }
            finally {
                accessToken = null;
            }
        },
        async platformLogout() {
            try {
                const response = await instance.post("/auth/platform/logout");
                const logoutUrl = response.data.logoutUrl;
                return typeof logoutUrl === "string" ? logoutUrl : undefined;
            }
            catch {
                return undefined;
            }
        },
        async subscribeRealtimeNotifications(listener) {
            await Authenticated(async () => Promise.resolve());
            if (accessToken === null) {
                throw new InternalApiError("An authenticated session is required.", 401);
            }
            const socket = io(realtimeOrigin, {
                auth: { accessToken },
                autoConnect: false,
                transports: ["websocket", "polling"],
                withCredentials: true,
            });
            const delivered = new Set();
            let refreshing = false;
            socket.on(RealtimeNotificationEventName, (input) => {
                const event = RealtimeNotificationEventSchema.parse(input);
                const deduplicationKey = `${event.eventId}:${event.notificationId}`;
                if (delivered.has(deduplicationKey)) {
                    return;
                }
                delivered.add(deduplicationKey);
                if (delivered.size > 1_000) {
                    const oldest = delivered.values().next().value;
                    if (typeof oldest === "string") {
                        delivered.delete(oldest);
                    }
                }
                listener(event);
            });
            socket.on("connect_error", (error) => {
                if (error.message !== "AUTHENTICATION_REQUIRED" || refreshing) {
                    return;
                }
                refreshing = true;
                void Refresh()
                    .then(() => {
                    if (accessToken === null) {
                        socket.disconnect();
                        return;
                    }
                    socket.auth = { accessToken };
                    socket.connect();
                })
                    .catch(() => {
                    socket.disconnect();
                })
                    .finally(() => {
                    refreshing = false;
                });
            });
            socket.connect();
            return () => {
                socket.removeAllListeners();
                socket.disconnect();
            };
        },
        async notifications(query = {}, signal) {
            const parsed = NotificationListQuerySchema.parse(query);
            return Authenticated(async () => {
                const response = await instance.get("/notifications", {
                    headers: AuthorizationHeaders(),
                    params: {
                        ...(parsed.casePublicId === undefined ? {} : { casePublicId: parsed.casePublicId }),
                        ...(parsed.cursor === undefined ? {} : { cursor: parsed.cursor }),
                        ...(parsed.from === undefined ? {} : { from: parsed.from }),
                        pageSize: parsed.pageSize,
                        ...(parsed.to === undefined ? {} : { to: parsed.to }),
                        unreadOnly: parsed.unreadOnly ? "true" : "false",
                    },
                    ...(signal === undefined ? {} : { signal }),
                });
                return NotificationListResponseSchema.parse(response.data);
            });
        },
        async markNotificationRead(notificationId) {
            if (!z.uuid().safeParse(notificationId).success) {
                throw new Error("The notification identifier is invalid.");
            }
            return Authenticated(async () => {
                const response = await instance.post(`/notifications/${encodeURIComponent(notificationId)}/read`, undefined, { headers: AuthorizationHeaders() });
                return NotificationReadResponseSchema.parse(response.data);
            });
        },
        async markAllNotificationsRead() {
            return Authenticated(async () => {
                const response = await instance.post("/notifications/read-all", undefined, {
                    headers: AuthorizationHeaders(),
                });
                return NotificationMarkAllReadResponseSchema.parse(response.data);
            });
        },
        async listFollowUps(publicId, query = {}, signal) {
            const parsed = CaseFollowUpListQuerySchema.parse(query);
            return Authenticated(async () => {
                const response = await instance.get(`${PublicIdPath(publicId)}/follow-ups`, {
                    headers: AuthorizationHeaders(),
                    params: { scope: parsed.scope },
                    ...(signal === undefined ? {} : { signal }),
                });
                return CaseFollowUpListResponseSchema.parse(response.data);
            });
        },
        async listWorkspaceFollowUps(query = {}, signal) {
            const parsed = WorkspaceFollowUpListQuerySchema.parse(query);
            return Authenticated(async () => {
                const response = await instance.get("/follow-ups", {
                    headers: AuthorizationHeaders(),
                    params: parsed,
                    ...(signal === undefined ? {} : { signal }),
                });
                return WorkspaceFollowUpListResponseSchema.parse(response.data);
            });
        },
        async createFollowUp(publicId, body) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/follow-ups`, CreateCaseFollowUpRequestSchema.parse(body), { headers: AuthorizationHeaders() });
                return CaseFollowUpItemSchema.parse(response.data);
            });
        },
        async completeFollowUp(publicId, followUpId) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/follow-ups/${UuidPath(followUpId, "Follow-up")}/completions`, undefined, { headers: AuthorizationHeaders() });
                return CaseFollowUpItemSchema.parse(response.data);
            });
        },
        async deferFollowUp(publicId, followUpId) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/follow-ups/${UuidPath(followUpId, "Follow-up")}/deferrals`, undefined, { headers: AuthorizationHeaders() });
                return CaseFollowUpItemSchema.parse(response.data);
            });
        },
        async markFollowUpNotCompleted(publicId, followUpId) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/follow-ups/${UuidPath(followUpId, "Follow-up")}/not-completions`, undefined, { headers: AuthorizationHeaders() });
                return CaseFollowUpItemSchema.parse(response.data);
            });
        },
        async rescheduleFollowUp(publicId, followUpId, body) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/follow-ups/${UuidPath(followUpId, "Follow-up")}/reschedules`, RescheduleCaseFollowUpRequestSchema.parse(body), { headers: AuthorizationHeaders() });
                return CaseFollowUpItemSchema.parse(response.data);
            });
        },
        async cancelFollowUp(publicId, followUpId, body = {}) {
            return Authenticated(async () => {
                const response = await instance.post(`${PublicIdPath(publicId)}/follow-ups/${UuidPath(followUpId, "Follow-up")}/cancellations`, CancelCaseFollowUpRequestSchema.parse(body), { headers: AuthorizationHeaders() });
                return CaseFollowUpItemSchema.parse(response.data);
            });
        },
        async list(query, signal) {
            const parsed = InternalCaseListQuerySchema.parse(query);
            return Authenticated(async () => {
                const response = await instance.get("/cases", {
                    headers: AuthorizationHeaders(),
                    params: parsed,
                    ...(signal === undefined ? {} : { signal }),
                });
                return InternalCaseListResponseSchema.parse(response.data);
            });
        },
        async detail(publicId, signal) {
            return Authenticated(async () => {
                const response = await instance.get(PublicIdPath(publicId), {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return InternalCaseDetailResponseSchema.parse(response.data);
            });
        },
        async revealAccessToken(publicId, signal) {
            return Authenticated(async () => {
                const response = await instance.get(`${PublicIdPath(publicId)}/access-token`, {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return RevealCaseAccessTokenResponseSchema.parse(response.data);
            });
        },
        async responseWorkflow(publicId, signal) {
            return Authenticated(async () => {
                const response = await instance.get(`${PublicIdPath(publicId)}/responses`, {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return CaseResponseWorkflowSchema.parse(response.data);
            });
        },
        async saveResponseDraft(input) {
            return MutateResponse("/responses/drafts", input, (body) => SaveResponseDraftRequestSchema.parse(body));
        },
        async submitResponseDraft(input) {
            return MutateResponse("/responses/submissions", input, (body) => SubmitResponseDraftRequestSchema.parse(body));
        },
        async withdrawResponseDraft(input) {
            return MutateResponse("/responses/withdrawals", input, (body) => WithdrawResponseDraftRequestSchema.parse(body));
        },
        async decideResponse(input) {
            return MutateResponse("/responses/decisions", input, (body) => DecideResponseDraftRequestSchema.parse(body));
        },
        async dispatchResponseLetter(input) {
            return MutateResponse("/responses/letter-dispatches", input, (body) => DispatchLetterResponseRequestSchema.parse(body));
        },
        async confirmResponseEmail(input) {
            return MutateResponse("/responses/email-confirmations", input, (body) => ConfirmEmailResponseRequestSchema.parse(body));
        },
        async assign(input) {
            return Mutate("/assignments", input, (body) => AssignInternalCaseRequestSchema.parse(body));
        },
        async changePriority(input) {
            return Mutate("/priority", input, (body) => ChangeInternalCasePriorityRequestSchema.parse(body));
        },
        async changeBranch(input) {
            return Mutate("/branch", input, (body) => ChangeInternalCaseBranchRequestSchema.parse(body));
        },
        async transition(input) {
            return Mutate("/transitions", input, (body) => TransitionInternalCaseRequestSchema.parse(body));
        },
        async reopen(input) {
            return Mutate("/reopen", input, (body) => ReopenInternalCaseRequestSchema.parse(body));
        },
        async reclassify(input) {
            return Mutate("/classification", input, (body) => ReclassifyInternalCaseRequestSchema.parse(body));
        },
        async setCaseReason(input) {
            return Mutate("/reason", input, (body) => SetInternalCaseReasonRequestSchema.parse(body));
        },
        async addNote(input) {
            return Mutate("/notes", input, (body) => AddInternalCaseNoteRequestSchema.parse(body));
        },
        async changeNoteColor(input) {
            return Mutate("/notes/color", input, (body) => ChangeInternalCaseNoteColorRequestSchema.parse(body));
        },
        async correctNote(input) {
            return Mutate("/notes/corrections", input, (body) => CorrectInternalCaseNoteRequestSchema.parse(body));
        },
        async addTag(input) {
            return Mutate("/tags", input, (body) => AddInternalCaseTagRequestSchema.parse(body));
        },
        async removeTag(input) {
            return Mutate("/tags/removals", input, (body) => RemoveInternalCaseTagRequestSchema.parse(body));
        },
        async listPlatformCompanies(signal) {
            return Authenticated(async () => {
                const response = await instance.get("/platform/companies", {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyListResponseSchema.parse(response.data);
            });
        },
        async getPlatformCompany(companyId, signal) {
            return Authenticated(async () => {
                const response = await instance.get(`/platform/companies/${encodeURIComponent(companyId)}`, {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyDetailSchema.parse(response.data);
            });
        },
        async createPlatformCompany(input) {
            const body = CreateCompanyRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.post("/platform/companies", body, {
                    headers: AuthorizationHeaders(),
                });
                return CompanyProvisioningResultSchema.parse(response.data);
            });
        },
        async updatePlatformCompany(companyId, input) {
            const body = UpdateCompanyRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.patch(`/platform/companies/${encodeURIComponent(companyId)}`, body, { headers: AuthorizationHeaders() });
                return CompanyDetailSchema.parse(response.data);
            });
        },
        async resetPlatformCompanyAdministratorCredentials(companyId, input) {
            return Authenticated(async () => {
                const body = ResetCompanyAdministratorCredentialsRequestSchema.parse(input);
                const response = await instance.post(`/platform/companies/${encodeURIComponent(companyId)}/administrator/credentials`, body, { headers: AuthorizationHeaders() });
                return ResetCompanyAdministratorCredentialsResultSchema.parse(response.data);
            });
        },
        async updatePlatformBranding(companyId, input) {
            const body = UpdateCompanyBrandingRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.put(`/platform/companies/${encodeURIComponent(companyId)}/branding`, body, { headers: AuthorizationHeaders() });
                return UpdateCompanyBrandingRequestSchema.parse(response.data);
            });
        },
        async previewPlatformBrandingLogo(companyId, input) {
            const body = PreviewCompanyBrandingLogoRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.post(`/platform/companies/${encodeURIComponent(companyId)}/branding/logo-preview`, body, { headers: AuthorizationHeaders() });
                return PreviewCompanyBrandingLogoResultSchema.parse(response.data);
            });
        },
        async createPlatformBranch(companyId, input) {
            const body = CreateCompanyBranchRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.post(`/platform/companies/${encodeURIComponent(companyId)}/branches`, body, { headers: AuthorizationHeaders() });
                return CompanyBranchSchema.parse(response.data);
            });
        },
        async updatePlatformBranch(companyId, branchId, input) {
            const body = UpdateCompanyBranchRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.patch(`/platform/companies/${encodeURIComponent(companyId)}/branches/${encodeURIComponent(branchId)}`, body, { headers: AuthorizationHeaders() });
                return CompanyBranchSchema.parse(response.data);
            });
        },
        async createPlatformDomain(companyId, input) {
            const body = CreateCompanyDomainRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.post(`/platform/companies/${encodeURIComponent(companyId)}/domains`, body, { headers: AuthorizationHeaders() });
                return CompanyDomainSchema.parse(response.data);
            });
        },
        async listPlatformPlans(signal) {
            return Authenticated(async () => {
                const response = await instance.get("/platform/plans", {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return PlanListResponseSchema.parse(response.data);
            });
        },
        async updatePlatformPlan(planId, input) {
            const body = UpdatePlanRequestSchema.parse(input);
            if (!Number.isSafeInteger(planId) || planId <= 0) {
                throw new Error("The plan identifier is invalid.");
            }
            return Authenticated(async () => {
                const response = await instance.patch(`/platform/plans/${encodeURIComponent(String(planId))}`, body, { headers: AuthorizationHeaders() });
                return PlanSchema.parse(response.data);
            });
        },
        async assignPlatformCompanyPlan(companyId, input) {
            const body = AssignCompanyPlanRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.put(`/platform/companies/${UuidPath(companyId, "Company")}/plan`, body, { headers: AuthorizationHeaders() });
                return PlanSchema.parse(response.data);
            });
        },
        async getCompanyEntitlements(signal) {
            return Authenticated(async () => {
                const response = await instance.get("/entitlements", {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyEntitlementsSchema.parse(response.data);
            });
        },
        async getCompanyProfile(signal) {
            return Authenticated(async () => {
                const response = await instance.get("/company-profile", {
                    headers: AuthorizationHeaders(),
                    ...(signal === undefined ? {} : { signal }),
                });
                return CompanyProfileSchema.parse(response.data);
            });
        },
        async updateCompanyProfile(input) {
            const body = UpdateCompanyProfileRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.put("/company-profile", body, {
                    headers: AuthorizationHeaders(),
                });
                return CompanyProfileSchema.parse(response.data);
            });
        },
        async updateCompanyProfileBranding(input) {
            const body = UpdateCompanyBrandingRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.put("/company-profile/branding", body, {
                    headers: AuthorizationHeaders(),
                });
                return CompanyProfileSchema.parse(response.data);
            });
        },
        async previewCompanyProfileBrandingLogo(input) {
            const body = PreviewCompanyBrandingLogoRequestSchema.parse(input);
            return Authenticated(async () => {
                const response = await instance.post("/company-profile/branding/logo-preview", body, { headers: AuthorizationHeaders() });
                return PreviewCompanyBrandingLogoResultSchema.parse(response.data);
            });
        },
        async verifyPlatformDomain(companyId, domainId) {
            return Authenticated(async () => {
                const response = await instance.post(`/platform/companies/${encodeURIComponent(companyId)}/domains/${encodeURIComponent(domainId)}/verify`, {}, { headers: AuthorizationHeaders() });
                return DomainVerificationResultSchema.parse(response.data);
            });
        },
    };
    return Object.freeze(client);
}
